import {
	Alert,
	AlertDescription,
	AlertTitle,
} from '@dealstack/frontend/shared/components/Alert/Alert';
import { GenericBooleanStatus } from '@dealstack/frontend/shared/components/GenericStatus/variants/GenericBooleanStatus/GenericBooleanStatus';
import {
	bulkEditMipInvestmentsUploadFile,
	getMipInvestmentsData,
} from '@dealstack/frontend/shared/dataExchange/services/god.service';
import { useDSToast } from '@dealstack/frontend/shared/mechanism/toast/useDSToast';
import { useWrappedRequest } from '@dealstack/frontend/shared/other/react/ApiWrapper';
import { asyncLoadExceljs } from '@dealstack/frontend/shared/utils/asyncLoadExceljs';
import { EXCEL_SPREADSHEET_TYPE } from '@dealstack/shared/lib/common/business/excel';
import * as ApiV1 from '@dealstack/shared/lib/common/dataExchange/apiV1';
import { getSecurityTypeNameFromType } from '@dealstack/shared/lib/common/securities/securities';
import { DATE_FORMATS, formatDateToUI } from '@dealstack/shared/lib/format/dates/date';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import {
	DataInvestmentWorkflowTct,
	DataShareAssociationTct,
	DataSimpleInvestorTct,
} from '@dealstack/shared/lib/generated';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { useQuery } from '@tanstack/react-query';
import {
	ColumnDef,
	getCoreRowModel,
	getSortedRowModel,
	SortingState,
	useReactTable,
} from '@tanstack/react-table';
import { saveAs } from 'file-saver';
import { useMemo, useRef, useState } from 'react';
import { useVirtual } from 'react-virtual';
import type { RowVirtualizer } from '@dealstack/frontend/shared/utils/reactVirtualTypes';

const EMPTY_DATA: InvestmentTableItemType[] = [];

export enum INVESTMENT_TABLE_ITEM_TYPE {
	TRANSACTION_SHARE_ASSOSCIATION = 'Transaction',
	SHARE_ASSOSCIATION = 'Share transfer',
}
type InvestmentTableItemType =
	| {
			investor: DataSimpleInvestorTct;
			investment: DataInvestmentWorkflowTct;
			type: INVESTMENT_TABLE_ITEM_TYPE.TRANSACTION_SHARE_ASSOSCIATION;
	  }
	| {
			shareAssociation: DataShareAssociationTct;
			investor: DataSimpleInvestorTct;
			investment: DataInvestmentWorkflowTct;
			type: INVESTMENT_TABLE_ITEM_TYPE.SHARE_ASSOSCIATION;
	  };

export enum CURRENT_VISIBLE_MODAL {
	NONE,
	BULK_CREATE,
	BULK_EDIT,
	BULK_EDIT_WARNING,
}

export const useInvestmentsController = (mipId: string) => {
	const toastApi = useDSToast();
	const [currentlyOpenModal, setCurrentlyOpenModal] = useState<CURRENT_VISIBLE_MODAL>(
		CURRENT_VISIBLE_MODAL.NONE
	);
	const [sorting, setSorting] = useState<SortingState>([
		{
			desc: false,
			id: 'investor_email',
		},
	]);
	const [skippedEditIds, setSkippedEditIds] = useState<string[] | null>(null);

	const getMipInvestmentsDataRequest = useQuery({
		queryKey: [formatUnicorn(API_PATHS.GODMODE_MIP_MIPID_INVESTMENTS_GET, { mipId })],
		queryFn: ({ signal }) =>
			getMipInvestmentsData(
				formatUnicorn(API_PATHS.GODMODE_MIP_MIPID_INVESTMENTS_GET, { mipId }),
				signal
			),
		meta: { suppressErrorToast: true },
	});

	const investmentsListData = useMemo((): InvestmentTableItemType[] => {
		const investors = getMipInvestmentsDataRequest?.data?.payload?.investors;
		const investments = investors
			?.map((investor) => {
				const investmentWorkflow = (investor as unknown as Record<string, unknown[]>)
					.investmentWorkflows;
				return investmentWorkflow.map((investment) => {
					return {
						investor,
						investment,
					};
				});
			})
			.flat()
			.map((item) => {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				const sha = (item as any).investment.shareAssociations;
				return [
					{
						...item,
						type: INVESTMENT_TABLE_ITEM_TYPE.TRANSACTION_SHARE_ASSOSCIATION,
					} as InvestmentTableItemType,
					...sha.map((sa) => {
						return {
							...item,
							shareAssociation: sa,
							type: INVESTMENT_TABLE_ITEM_TYPE.SHARE_ASSOSCIATION,
						} as InvestmentTableItemType;
					}),
				];
			})
			.flat();
		return investments ?? EMPTY_DATA;
	}, [getMipInvestmentsDataRequest?.data?.payload]);

	const manageListInvestmentsColumns: ColumnDef<InvestmentTableItemType>[] = useMemo(() => {
		return [
			{
				id: 'type',
				accessorFn: (row) => row?.type,
				size: 25,
				header: () => <span className="support-app-compact-text">Type</span>,
				cell: (info) => {
					return (
						<span className="support-app-compact-text">
							{info?.getValue() as string}
						</span>
					);
				},
			},
			{
				id: 'investmentWorkflowId',
				size: 80,
				header: () => <span className="support-app-compact-text">Transaction Id</span>,
				cell: (info) => {
					const original = info.row.original;
					const investmentWorkflowId =
						original.type ===
							INVESTMENT_TABLE_ITEM_TYPE.TRANSACTION_SHARE_ASSOSCIATION &&
						original.investment.id;
					return <span className="support-app-compact-text">{investmentWorkflowId}</span>;
				},
			},
			{
				id: 'investorId',
				accessorFn: (row) => row?.investor.id,
				size: 50,
				header: () => <span className="support-app-compact-text">Investor Id</span>,
				cell: (info) => {
					return (
						<span className="support-app-compact-text">
							{info?.getValue() as string}
						</span>
					);
				},
			},
			{
				id: 'step',
				accessorFn: (row) => row?.investment.completionStep,
				size: 30,
				header: () => (
					<span className="support-app-compact-text">InvestmentWorkflow step</span>
				),
				cell: (info) => {
					if (
						info.row.original.type !==
						INVESTMENT_TABLE_ITEM_TYPE.TRANSACTION_SHARE_ASSOSCIATION
					) {
						return null;
					}
					return (
						<span className="support-app-compact-text">
							{(info?.getValue() as string) ?? 'Not started'}
						</span>
					);
				},
			},
			{
				id: 'InvestmentWorkflow Completed',
				accessorFn: (row) => row?.investment.isCompleted,
				size: 25,
				header: () => <span className="support-app-compact-text">Completed</span>,
				cell: (info) => {
					if (
						info.row.original.type !==
						INVESTMENT_TABLE_ITEM_TYPE.TRANSACTION_SHARE_ASSOSCIATION
					) {
						return null;
					}
					return <GenericBooleanStatus value={(info?.getValue() as boolean) || false} />;
				},
			},
			{
				id: 'investor_email',
				accessorFn: (row) => row?.investor?.user?.email,
				size: 40,
				header: () => <span>Investor email</span>,
				cell: (info) => {
					return (
						<span className="support-app-compact-text">
							{info?.getValue() as string}
						</span>
					);
				},
			},
			{
				id: 'Transaction date',
				size: 30,
				header: () => <span>Transaction date</span>,
				cell: (info) => {
					const original = info.row.original;
					const transactionDate =
						original.type ===
							INVESTMENT_TABLE_ITEM_TYPE.TRANSACTION_SHARE_ASSOSCIATION &&
						original.investment.updatedAt;
					if (!transactionDate) {
						return null;
					}
					if (!original.investment.isCompleted) {
						return null;
					}
					const formattedDate = formatDateToUI(
						new Date(transactionDate),
						DATE_FORMATS.EXCEL
					);
					return <span className="support-app-compact-text">{formattedDate}</span>;
				},
			},
			{
				id: 'Security name',
				size: 25,
				header: () => <span className="support-app-compact-text">Security name</span>,
				cell: (info) => {
					const original = info.row.original;
					const secName =
						original.type === INVESTMENT_TABLE_ITEM_TYPE.SHARE_ASSOSCIATION &&
						original.shareAssociation.security.name;
					return <span className="support-app-compact-text">{secName}</span>;
				},
			},
			{
				id: 'Security type',
				size: 20,
				header: () => <span className="support-app-compact-text">Security type</span>,
				cell: (info) => {
					const original = info.row.original;
					const secType =
						original.type === INVESTMENT_TABLE_ITEM_TYPE.SHARE_ASSOSCIATION &&
						getSecurityTypeNameFromType(original.shareAssociation.security.type);
					return <span className="support-app-compact-text">{secType}</span>;
				},
			},
			{
				id: 'Is negative',
				size: 20,
				header: () => <span>Is negative</span>,
				cell: (info) => {
					const original = info.row.original;
					const isNegative =
						original.type === INVESTMENT_TABLE_ITEM_TYPE.SHARE_ASSOSCIATION &&
						original.shareAssociation.isNegative;
					return <GenericBooleanStatus value={isNegative || false} />;
				},
			},
			{
				id: 'count',
				size: 5,
				header: () => <span>Count</span>,
				cell: (info) => {
					const original = info.row.original;
					const count =
						original.type === INVESTMENT_TABLE_ITEM_TYPE.SHARE_ASSOSCIATION &&
						original.shareAssociation.number;
					return count;
				},
			},
			{
				id: 'cost',
				size: 5,
				header: () => <span>Cost</span>,
				cell: (info) => {
					const original = info.row.original;
					const cost =
						original.type === INVESTMENT_TABLE_ITEM_TYPE.SHARE_ASSOSCIATION &&
						original.shareAssociation.cost;
					return cost;
				},
			},
		];
	}, [getMipInvestmentsDataRequest?.isLoading]);

	const tanstackTable = useReactTable({
		data: investmentsListData,
		columns: manageListInvestmentsColumns,
		enableSorting: true,
		onSortingChange: setSorting,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		state: {
			sorting,
		},
	});
	const tableContainerRef = useRef<HTMLTableElement>(null);
	const { rows } = tanstackTable.getRowModel();
	const rowVirtualizer: RowVirtualizer = useVirtual({
		parentRef: tableContainerRef,
		size: rows.length,
		overscan: 10,
	});

	const onRefreshTable = () => {
		void getMipInvestmentsDataRequest.refetch();
	};

	const onDownloadXLSX = async () => {
		const ExcelJS = await asyncLoadExceljs();
		const workbook = new ExcelJS.Workbook();
		workbook.created = new Date();
		const dataWorksheet = workbook.addWorksheet(`bulk-edit-investments`);
		const columns = [
			'investmentWorkflowId',
			'saId',
			'type',
			'investor id',
			'investor email',
			'transaction date (editable)',
			'investmentWorkflow completed',
			'investmentWorkflow step',
			'security name',
			'security type',
			'negative',
			'count (editable)',
			'cost (editable)',
		];

		const spreadsheetData = investmentsListData.map((item) => {
			if (item.type === INVESTMENT_TABLE_ITEM_TYPE.TRANSACTION_SHARE_ASSOSCIATION) {
				return {
					investmentWorkflowId: item.investment.id,
					saId: '',
					type: item.type,
					'investor id': item.investor.id,
					'investor email': item.investor.user.email,
					'transaction date (editable)': item.investment.isCompleted
						? formatDateToUI(new Date(item.investment.updatedAt), DATE_FORMATS.EXCEL)
						: null,
					'investmentWorkflow completed': item.investment.isCompleted,
					'investmentWorkflow step': item.investment.completionStep,
					'security name': null,
					'security type': null,
					negative: null,
					'count (editable)': null,
					'cost (editable)': null,
				};
			}
			return {
				investmentWorkflowId: item.investment.id,
				saId: item.shareAssociation.id,
				type: item.type,
				'investor id': item.investor.id,
				'investor email': item.investor.id,
				'transaction date (editable)': null,
				'investmentWorkflow completed': null,
				'investmentWorkflow step': null,
				'security name': item.shareAssociation.security.name,
				'security type': item.shareAssociation.security.type,
				negative: item.shareAssociation.isNegative,
				'count (editable)': item.shareAssociation.number,
				'cost (editable)': item.shareAssociation.cost,
			};
		});
		if (!spreadsheetData.length) {
			return null;
		}

		dataWorksheet.columns = columns.map((name) => ({
			header: name,
			key: name,
			width: 20,
		}));

		dataWorksheet.addRows(
			spreadsheetData?.map((item) => ({
				...item,
			}))
		);
		const xls64 = await workbook.xlsx.writeBuffer();
		saveAs(
			new Blob([xls64], {
				type: EXCEL_SPREADSHEET_TYPE,
			}),
			`update-share-association-mip-${mipId}.xlsx`
		);
	};

	const bulkEditUpload = useWrappedRequest<
		{ formData: FormData; onUploadProgress?: (percent: number) => void },
		ApiV1.Response<string[]>
	>(
		bulkEditMipInvestmentsUploadFile(mipId),
		{},
		{
			onSuccessDefault: ({ payload }) => {
				void getMipInvestmentsDataRequest.refetch();
				const ids = Array.isArray(payload) ? payload : [];
				if (ids.length > 0) {
					setSkippedEditIds(ids);
					toastApi.success({
						title: 'Bulk edit completed',
						message: 'Some transactions were skipped',
					});
				} else {
					setSkippedEditIds(null);
					setCurrentlyOpenModal(CURRENT_VISIBLE_MODAL.NONE);
					toastApi.success({ title: 'Bulk edit completed' });
				}
			},
		}
	);

	const renderBulkEditAlert = () => {
		if (!skippedEditIds) {
			return null;
		}
		return (
			<Alert size="m" state="warning" closeButton onClose={() => setSkippedEditIds(null)}>
				<AlertTitle>Some transactions were skipped</AlertTitle>
				<AlertDescription>
					<ul style={{ margin: 0, paddingLeft: '1rem' }}>
						{skippedEditIds.map((id) => (
							<li key={id}>{id}</li>
						))}
					</ul>
				</AlertDescription>
			</Alert>
		);
	};

	return {
		bulkEditUpload,
		renderBulkEditAlert,
		currentlyOpenModal,
		setCurrentlyOpenModal,
		onDownloadXLSX,
		rows,
		onRefreshTable,
		tanstackTable,
		getMipInvestmentsDataRequest,
		rowVirtualizer,
		tableContainerRef,
	};
};
